import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentNote, RichTextDocument } from '@servicesai/api-client';
import {
  EmptyState,
  GlassButton,
  GlassCard,
  GlassInput,
  StatusPill,
  useI18n,
} from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import { S } from '../../strings.js';
import {
  plainTextToRichDocument,
  RichTextDocumentView,
  RichTextEditor,
} from './RichTextEditor.js';

type NoteAction =
  | { kind: 'create'; body: string; bodyDoc: RichTextDocument; labels: string[] }
  | { kind: 'update'; note: AgentNote; body: string; bodyDoc: RichTextDocument; labels: string[] }
  | { kind: 'archive'; noteId: string };

function parseLabels(value: string): string[] {
  return [...new Set(value.split(',').map((label) => label.trim()).filter(Boolean))];
}

export function AgentNotesPanel({
  slug,
  selectedNoteIds,
  onSelectedNoteIdsChange,
}: {
  slug: string;
  selectedNoteIds: string[];
  onSelectedNoteIdsChange: (noteIds: string[]) => void;
}) {
  const { l, lang } = useI18n();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [body, setBody] = useState('');
  const [bodyDoc, setBodyDoc] = useState<RichTextDocument | null>(null);
  const [labels, setLabels] = useState('');
  const [editing, setEditing] = useState<AgentNote | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const notesQuery = useQuery({
    queryKey: ['agent-notes', slug, showArchived],
    queryFn: () => admin().agentNotes(slug, showArchived),
  });
  const notes = useMemo(() => notesQuery.data?.items ?? [], [notesQuery.data?.items]);

  useEffect(() => {
    const available = selectedNoteIds.filter((id) => notes.some((note) => note.id === id && !note.archived_at));
    if (available.length !== selectedNoteIds.length) onSelectedNoteIdsChange(available);
  }, [notes, onSelectedNoteIdsChange, selectedNoteIds]);

  const noteMutation = useMutation({
    mutationFn: async (action: NoteAction) => {
      if (action.kind === 'create') {
        return admin().createAgentNote(slug, {
          body: action.body,
          bodyDoc: action.bodyDoc,
          bodyFormat: 'rich_text_v1',
          labels: action.labels,
        });
      }
      if (action.kind === 'update') {
        return admin().updateAgentNote(slug, action.note.id, {
          body: action.body,
          bodyDoc: action.bodyDoc,
          bodyFormat: 'rich_text_v1',
          labels: action.labels,
          expectedRevision: action.note.revision,
        });
      }
      return admin().archiveAgentNote(slug, action.noteId);
    },
    onSuccess: async (_result, action) => {
      setNotice(
        action.kind === 'create'
          ? l(S.noteCreated)
          : action.kind === 'update'
            ? l(S.noteUpdated)
            : l(S.noteArchived),
      );
      setBody('');
      setBodyDoc(null);
      setLabels('');
      setEditing(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agent-notes', slug] }),
        queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] }),
      ]);
    },
  });

  const beginEdit = (note: AgentNote) => {
    setEditing(note);
    setBody(note.body);
    setBodyDoc(note.body_doc);
    setLabels(note.labels.join(', '));
    setNotice(null);
  };
  const resetForm = () => {
    setEditing(null);
    setBody('');
    setBodyDoc(null);
    setLabels('');
  };
  const submit = () => {
    const trimmed = body.trim();
    if (!trimmed || trimmed.length > 50_000 || noteMutation.isPending) return;
    const document = bodyDoc ?? plainTextToRichDocument(trimmed);
    if (editing) {
      noteMutation.mutate({
        kind: 'update',
        note: editing,
        body: trimmed,
        bodyDoc: document,
        labels: parseLabels(labels),
      });
    } else {
      noteMutation.mutate({
        kind: 'create',
        body: trimmed,
        bodyDoc: document,
        labels: parseLabels(labels),
      });
    }
  };

  return (
    <div className="space-y-4">
      <GlassCard className="p-5" strong>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-extrabold">{l(S.notesContext)}</h2>
              {selectedNoteIds.length > 0 && <StatusPill state="in_progress" label={`${selectedNoteIds.length} ${l(S.selected)}`} />}
            </div>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-faint">{l(S.notesContextHint)}</p>
          </div>
          <label className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-aeblack-100 bg-white px-3 py-2 text-xs font-bold">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
              className="h-4 w-4"
            />
            {l(S.showArchived)}
          </label>
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-extrabold">{editing ? l(S.editNote) : l(S.addNote)}</h3>
            <p className="mt-1 text-xs text-ink-faint">{l(S.noteComposerHint)}</p>
          </div>
          {editing && <GlassButton size="sm" onClick={resetForm}>{l(S.cancel)}</GlassButton>}
        </div>
        <div className="mt-4">
          <div className="mb-1 text-xs font-bold text-ink-soft">{l(S.note)}</div>
          <RichTextEditor
            value={bodyDoc}
            fallbackText={body}
            lang={lang}
            ariaLabel={l(S.note)}
            onChange={(document, plainText) => {
              setBodyDoc(document);
              setBody(plainText);
            }}
          />
          {!body.trim() && <p className="mt-1 text-[10px] text-ink-faint">{l(S.notePlaceholder)}</p>}
        </div>
        <label className="mt-3 block text-xs font-bold text-ink-soft">
          {l(S.labels)}
          <GlassInput
            value={labels}
            onChange={(event) => setLabels(event.target.value)}
            className="mt-1"
            placeholder={l(S.labelsHint)}
          />
        </label>
        <div className="mt-3 flex justify-end">
          <GlassButton
            variant="primary"
            disabled={!body.trim() || body.length > 50_000 || noteMutation.isPending}
            onClick={submit}
          >
            {noteMutation.isPending ? l(S.saving) : editing ? l(S.saveChanges) : l(S.saveNote)}
          </GlassButton>
        </div>
      </GlassCard>

      {notice && (
        <div className="aegov-alert alert-success" role="status">
          <div className="alert-content">{notice}</div>
        </div>
      )}
      {noteMutation.error && (
        <div className="aegov-alert alert-error" role="alert">
          <div className="alert-content">{l(S.actionFailed)}: {(noteMutation.error as Error).message}</div>
        </div>
      )}

      <div className="rounded-xl border border-aegold-200 bg-aegold-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-extrabold">{l(S.useWithConfigurationAi)}</div>
            <p className="mt-0.5 text-xs text-ink-soft">{l(S.useWithConfigurationAiHint)}</p>
          </div>
          <StatusPill state={selectedNoteIds.length ? 'in_progress' : 'draft'} label={`${selectedNoteIds.length} ${l(S.selected)}`} />
        </div>
      </div>

      {notesQuery.isLoading ? (
        <GlassCard><EmptyState title={l(S.connecting)} /></GlassCard>
      ) : notesQuery.error ? (
        <div className="aegov-alert alert-error" role="alert">
          <div className="alert-content">{l(S.loadFailed)}: {(notesQuery.error as Error).message}</div>
        </div>
      ) : notes.length === 0 ? (
        <GlassCard><EmptyState title={l(showArchived ? S.noArchivedNotes : S.noNotes)} hint={l(S.noNotesHint)} /></GlassCard>
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {notes.map((note) => {
            const isSelected = selectedNoteIds.includes(note.id);
            return (
              <GlassCard key={note.id} className="p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={Boolean(note.archived_at)}
                    onChange={(event) => onSelectedNoteIdsChange(
                      event.target.checked
                        ? [...selectedNoteIds, note.id]
                        : selectedNoteIds.filter((id) => id !== note.id),
                    )}
                    aria-label={`${l(S.useWithConfigurationAi)}: ${note.body.slice(0, 40)}`}
                    className="mt-1 h-4 w-4 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <RichTextDocumentView document={note.body_doc} fallback={note.body} />
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {note.labels.map((label) => (
                        <span key={label} className="rounded-md bg-aeblack-50 px-2 py-1 text-[10px] font-bold text-ink-faint">{label}</span>
                      ))}
                      {note.archived_at && <StatusPill state="rejected" label={l(S.archived)} />}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-aeblack-100 pt-3 text-[10px] text-ink-faint">
                      <span>{l(S.updated)} {new Date(note.updated_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}</span>
                      <span>r{note.revision}</span>
                      {!note.archived_at && (
                        <span className="ms-auto flex gap-2">
                          <GlassButton size="sm" onClick={() => beginEdit(note)}>{l(S.edit)}</GlassButton>
                          <GlassButton
                            size="sm"
                            variant="danger"
                            disabled={noteMutation.isPending}
                            onClick={() => noteMutation.mutate({ kind: 'archive', noteId: note.id })}
                          >
                            {l(S.archive)}
                          </GlassButton>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
